import React, { useMemo } from 'react'

import { VCardAttributes } from '@/lib/types/VCardT'
import { cn } from '@/lib/utils'
import { FOCUS_RING } from '@/components/livecontact/fieldStyles'
import VCardTag from '@/components/VCardSelectField/Tag'

type Props = {
  selectedVCards: Pick<
    VCardAttributes,
    'id' | 'representativeName' | 'avatar' | 'isAvatarGenerated' | 'mainColor'
  >[]
  inputValue: string
  onFocus?: () => void
  onBlur?: () => void
  onInputChange: (val: string) => void
  onRemoveVCard: (id: number) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  inputRef: React.RefObject<HTMLInputElement>
  placeholder: string
  isMultiple: boolean
  maxDisplay?: number
}

/** Chip + search input on the field surface, with a "+N" overflow chip. */
const VCardSelectInput = ({
  selectedVCards,
  inputValue,
  onInputChange,
  onRemoveVCard,
  onKeyDown,
  onFocus,
  onBlur,
  inputRef,
  placeholder,
  isMultiple,
  maxDisplay,
}: Props) => {
  const displayedVCards = useMemo(
    () => (maxDisplay ? selectedVCards.slice(0, maxDisplay) : selectedVCards),
    [selectedVCards, maxDisplay]
  )

  const hiddenCount = useMemo(() => {
    if (!maxDisplay || selectedVCards.length <= maxDisplay) return 0
    return selectedVCards.length - maxDisplay
  }, [selectedVCards.length, maxDisplay])

  return (
    <div
      className={cn(
        'flex min-h-8 w-full cursor-text flex-wrap items-center gap-1 rounded-[8px] bg-surface px-1.5 py-1 shadow-input transition-shadow duration-[160ms] ease-out-strong',
        `focus-within:shadow-[${FOCUS_RING}]`
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {displayedVCards.map((vCard) => (
        <VCardTag key={vCard.id} vCard={vCard} onRemove={onRemoveVCard} />
      ))}
      {hiddenCount > 0 && (
        <span className="inline-flex h-6 items-center rounded-full bg-surface-2 px-2 text-[12px] font-medium text-ink-3 dark:bg-surface-3">
          +{hiddenCount}
        </span>
      )}
      {(selectedVCards.length === 0 || isMultiple) && (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-6 min-w-[120px] flex-1 bg-transparent px-1 text-[14px] leading-[1.4] tracking-[-0.2px] text-ink outline-none placeholder:text-ink-4"
        />
      )}
    </div>
  )
}

export default VCardSelectInput
